<template>
  <v-container fluid class="ma-0 pa-2 d-flex justify-center">
    <v-card outlined width="100%" max-width="1050">
      <v-card-title class="align-start">
        <v-icon color="primary" class="mr-3 mt-1">
          mdi-lock-reset
        </v-icon>
        <div>
          <div class="text-h6">
            Passwort zurücksetzen
          </div>
          <div class="text-caption text--secondary font-weight-regular mt-1">
            Self-Service per E-Mail sicher konfigurieren. Die Funktion ist standardmäßig deaktiviert.
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          small
          text
          color="primary"
          :loading="loading"
          :disabled="requestRunning"
          @click="reloadSettings"
        >
          <v-icon left>
            mdi-refresh
          </v-icon>
          Neu laden
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4 pa-sm-6">
        <v-alert
          v-if="message"
          :type="messageType"
          dense
          text
        >
          {{ message }}
        </v-alert>

        <v-skeleton-loader
          v-if="loading && !loaded"
          type="article, article, actions"
        ></v-skeleton-loader>

        <template v-else-if="loaded">
          <div class="d-flex flex-wrap mb-4">
            <v-chip
              small
              dark
              class="mr-2 mb-2"
              :color="form.enabled ? 'success' : 'grey'"
            >
              <v-icon left small>
                {{ form.enabled ? 'mdi-check-circle' : 'mdi-cancel' }}
              </v-icon>
              {{ form.enabled ? 'Reset aktiv' : 'Reset deaktiviert' }}
            </v-chip>
            <v-chip
              small
              outlined
              class="mr-2 mb-2"
              :color="summary.smtpConfigured ? 'success' : 'warning'"
            >
              <v-icon left small>
                {{ summary.smtpConfigured ? 'mdi-email-check' : 'mdi-email-alert' }}
              </v-icon>
              {{ summary.smtpConfigured ? 'SMTP vollständig' : 'SMTP unvollständig' }}
            </v-chip>
            <v-chip
              small
              outlined
              class="mr-2 mb-2"
              :color="summary.passwordConfigured ? 'success' : 'warning'"
            >
              <v-icon left small>
                {{ summary.passwordConfigured ? 'mdi-lock-check' : 'mdi-lock-alert' }}
              </v-icon>
              {{ summary.passwordConfigured ? 'SMTP-Passwort hinterlegt' : 'Kein SMTP-Passwort' }}
            </v-chip>
            <v-chip
              small
              outlined
              class="mr-2 mb-2"
              :color="summary.emailParametersConfigured ? 'success' : 'warning'"
            >
              <v-icon left small>
                {{ summary.emailParametersConfigured ? 'mdi-account-check' : 'mdi-account-alert' }}
              </v-icon>
              {{ summary.emailParametersConfigured
                ? 'E-Mail-Felder zugeordnet' : 'E-Mail-Felder prüfen' }}
            </v-chip>
          </div>

          <v-form
            ref="settingsForm"
            v-model="formValid"
            @submit.prevent="saveSettings"
          >
            <p class="text-subtitle-1 font-weight-medium mb-1">
              Funktion und Kontozuordnung
            </p>
            <p class="text-caption text--secondary mb-3">
              Nur Werte der ausgewählten E-Mail-Nutzerparameter werden für die Kontosuche verwendet.
              Die öffentliche Antwort verrät nie, ob eine Adresse vorhanden ist.
            </p>

            <v-switch
              v-model="form.enabled"
              inset
              color="success"
              label="Passwort-Reset auf der Loginseite aktivieren"
              :disabled="requestRunning"
            ></v-switch>

            <v-alert
              v-if="!form.enabled"
              type="info"
              text
              dense
            >
              Solange die Funktion deaktiviert ist, wird auf der Loginseite keine
              Reset-Möglichkeit angezeigt und es werden keine Reset-E-Mails erzeugt.
            </v-alert>

            <v-alert
              v-else-if="!summary.emailParametersConfigured"
              type="warning"
              text
              dense
            >
              Mindestens ein gespeichertes E-Mail-Feld fehlt oder ist keiner Nutzergruppe
              mehr zugeordnet. Der Passwort-Reset bleibt bis zur Korrektur automatisch
              auf der Loginseite ausgeblendet.
            </v-alert>

            <v-autocomplete
              v-model="form.emailParameterIds"
              :items="availableEmailParameters"
              item-value="id"
              item-text="name"
              label="E-Mail-Felder für die Kontosuche"
              multiple
              chips
              small-chips
              deletable-chips
              :disabled="requestRunning"
              :rules="emailParameterRules"
              hint="Wählen Sie alle E-Mail-Felder aus, über die Benutzer ihr Konto zurücksetzen dürfen."
              persistent-hint
            ></v-autocomplete>

            <v-row dense class="mt-3">
              <v-col cols="12" md="8">
                <v-text-field
                  v-model.trim="form.baseUrl"
                  label="Öffentliche App-Adresse"
                  placeholder="https://beispiel.ogs-digital.de"
                  autocomplete="url"
                  :disabled="requestRunning"
                  :rules="baseUrlRules"
                  hint="Aus dieser vertrauenswürdigen Adresse wird der Reset-Link aufgebaut."
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.tokenTtlMinutes"
                  :items="ttlOptions"
                  label="Gültigkeit des Links"
                  :disabled="requestRunning"
                ></v-select>
              </v-col>
            </v-row>

            <v-checkbox
              v-if="summary.passwordConfigured"
              v-model="clearSmtpPassword"
              class="mt-0"
              label="Gespeichertes SMTP-Passwort beim Speichern löschen"
              :disabled="requestRunning"
              hint="Bei ausgeschalteter SMTP-Anmeldung wird ein vorhandenes Passwort automatisch entfernt."
              persistent-hint
              @change="handleClearSmtpPassword"
            ></v-checkbox>

            <v-divider class="my-6"></v-divider>

            <p class="text-subtitle-1 font-weight-medium mb-1">
              SMTP-Versand
            </p>
            <p class="text-caption text--secondary mb-3">
              Das SMTP-Passwort wird verschlüsselt gespeichert und niemals wieder angezeigt.
            </p>

            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.smtp.name"
                  label="Absendername"
                  maxlength="120"
                  :disabled="requestRunning"
                  :rules="smtpNameRules"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.trim="form.smtp.mail"
                  label="Absender-E-Mail-Adresse"
                  type="email"
                  maxlength="254"
                  autocomplete="email"
                  :disabled="requestRunning"
                  :rules="smtpMailRules"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.trim="form.smtp.host"
                  label="SMTP-Host"
                  maxlength="253"
                  autocomplete="off"
                  :disabled="requestRunning"
                  :rules="smtpHostRules"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="2">
                <v-text-field
                  v-model.number="form.smtp.port"
                  label="Port"
                  type="number"
                  min="1"
                  max="65535"
                  :disabled="requestRunning"
                  :rules="smtpPortRules"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="4">
                <v-select
                  v-model="form.smtp.secure"
                  :items="secureOptions"
                  label="Verschlüsselung"
                  :disabled="requestRunning"
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="form.smtp.auth"
                  inset
                  label="SMTP-Anmeldung verwenden"
                  :disabled="requestRunning"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.trim="form.smtp.user"
                  label="SMTP-Benutzername"
                  autocomplete="off"
                  maxlength="255"
                  :disabled="requestRunning || !form.smtp.auth"
                  :rules="smtpUserRules"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="smtpPassword"
                  label="Neues SMTP-Passwort"
                  :type="showSmtpPassword ? 'text' : 'password'"
                  :append-icon="showSmtpPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  autocomplete="new-password"
                  :disabled="requestRunning || !form.smtp.auth"
                  :rules="smtpPasswordRules"
                  :hint="summary.passwordConfigured
                    ? 'Leer lassen, um das vorhandene Passwort beizubehalten.'
                    : 'Das Passwort wird nur beim Speichern oder Testen übertragen.'"
                  persistent-hint
                  @click:append="showSmtpPassword = !showSmtpPassword"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <p class="text-subtitle-1 font-weight-medium mb-1">
              E-Mail-Layout
            </p>
            <p class="text-caption text--secondary mb-3">
              Verfügbare Platzhalter: <code>{{ placeholderLabel }}</code>.
              <code v-pre>{{ DISPLAY_NAME }}</code> entspricht derzeit dem Benutzernamen.
              Die sichere Reset-Schaltfläche wird zwischen Inhalt und Fußbereich eingefügt.
            </p>

            <v-text-field
              v-model="form.subject"
              label="Betreff"
              maxlength="180"
              counter="180"
              :disabled="requestRunning"
              :rules="subjectRules"
            ></v-text-field>

            <label class="text-body-2 font-weight-medium d-block mb-2">
              Inhalt vor der Reset-Schaltfläche
            </label>
            <HtmlEditor
              v-model="form.bodyHtml"
              label="Inhalt der Passwort-Reset-E-Mail"
              placeholder="E-Mail-Text eingeben ..."
              :disabled="requestRunning"
              :allow-images="false"
              @error="handleEditorError"
            />

            <v-row dense class="mt-4">
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="form.buttonText"
                  label="Beschriftung der Reset-Schaltfläche"
                  maxlength="80"
                  :disabled="requestRunning"
                  :rules="buttonTextRules"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.trim="form.buttonColor"
                  label="Schaltflächenfarbe"
                  placeholder="#1976D2"
                  maxlength="7"
                  :disabled="requestRunning"
                  :rules="buttonColorRules"
                >
                  <template #prepend-inner>
                    <span
                      class="color-preview"
                      :style="{ backgroundColor: validPreviewColor }"
                      aria-hidden="true"
                    ></span>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <label class="text-body-2 font-weight-medium d-block mb-2">
              Fußbereich
            </label>
            <HtmlEditor
              v-model="form.footerHtml"
              label="Fußbereich der Passwort-Reset-E-Mail"
              placeholder="Hinweise und Signatur eingeben ..."
              :disabled="requestRunning"
              :allow-images="false"
              @error="handleEditorError"
            />

            <v-expansion-panels class="mt-5" flat>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  E-Mail-Vorschau
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <iframe
                    class="mail-preview"
                    title="Vorschau der Passwort-Reset-E-Mail"
                    sandbox=""
                    :srcdoc="previewDocument"
                  ></iframe>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-divider class="my-6"></v-divider>

            <v-alert type="info" text dense>
              Speichern und SMTP-Test erfordern Ihr aktuelles Login-Passwort.
              Beim Aktivieren wird die SMTP-Verbindung serverseitig geprüft.
            </v-alert>

            <v-text-field
              ref="currentPassword"
              v-model="currentPassword"
              label="Aktuelles Login-Passwort"
              :type="showCurrentPassword ? 'text' : 'password'"
              :append-icon="showCurrentPassword ? 'mdi-eye-off' : 'mdi-eye'"
              autocomplete="current-password"
              :disabled="requestRunning"
              :rules="currentPasswordRules"
              @click:append="showCurrentPassword = !showCurrentPassword"
            ></v-text-field>

            <div class="d-flex flex-wrap justify-end mt-4">
              <v-btn
                outlined
                color="primary"
                class="mr-0 mr-sm-3 mb-2"
                :loading="testing"
                :disabled="requestRunning"
                @click="testSmtp"
              >
                <v-icon left>
                  mdi-email-check-outline
                </v-icon>
                SMTP testen
              </v-btn>
              <v-btn
                color="success"
                class="mb-2"
                :loading="saving"
                :disabled="requestRunning || !hasChanges"
                @click="saveSettings"
              >
                <v-icon left>
                  mdi-content-save
                </v-icon>
                Einstellungen speichern
              </v-btn>
            </div>
          </v-form>
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import HtmlEditor from '@/components/Funktionen/400/410-Kunden/HtmlEditor.vue';

const PLACEHOLDERS = '{{APP_NAME}}, {{DISPLAY_NAME}}, {{USERNAME}}, {{VALID_MINUTES}}';

function defaultForm() {
  return {
    enabled: false,
    emailParameterIds: [],
    baseUrl: '',
    tokenTtlMinutes: 30,
    smtp: {
      name: '',
      mail: '',
      host: '',
      port: 465,
      secure: 'ssl',
      auth: true,
      user: '',
    },
    subject: 'Passwort für {{APP_NAME}} zurücksetzen',
    bodyHtml: '<h2>Passwort zurücksetzen</h2><p>Hallo {{DISPLAY_NAME}},</p><p>für Ihr Konto bei <strong>{{APP_NAME}}</strong> wurde das Zurücksetzen des Passworts angefordert.</p>',
    buttonText: 'Passwort zurücksetzen',
    buttonColor: '#1976D2',
    footerHtml: '<p>Dieser Link ist {{VALID_MINUTES}} Minuten gültig und kann nur einmal verwendet werden.</p><p>Falls Sie das Zurücksetzen nicht angefordert haben, können Sie diese E-Mail ignorieren.</p>',
  };
}

function boolValue(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function defaultPublicBaseUrl() {
  const configuredBase = process.env.BASE_URL || '/';
  try {
    return new URL(configuredBase, window.location.href).href.replace(/\/+$/, '');
  } catch {
    return window.location.origin;
  }
}

export default {
  name: 'PasswortResetEinstellungen',
  components: { HtmlEditor },
  data: () => ({
    form: defaultForm(),
    loadedSnapshot: '',
    availableEmailParameters: [],
    loaded: false,
    loading: false,
    saving: false,
    testing: false,
    formValid: false,
    smtpPassword: '',
    clearSmtpPassword: false,
    currentPassword: '',
    showSmtpPassword: false,
    showCurrentPassword: false,
    message: '',
    messageType: 'info',
    summary: {
      passwordConfigured: false,
      smtpConfigured: false,
      emailParametersConfigured: false,
      invalidEmailParameterIds: [],
      revision: 0,
      updatedAt: 0,
      lastTestedAt: 0,
      lastTestResult: '',
    },
    ttlOptions: [
      { text: '15 Minuten', value: 15 },
      { text: '30 Minuten', value: 30 },
      { text: '60 Minuten', value: 60 },
      { text: '2 Stunden', value: 120 },
    ],
    secureOptions: [
      { text: 'SSL/TLS (meist Port 465)', value: 'ssl' },
      { text: 'STARTTLS (meist Port 587)', value: 'tls' },
    ],
  }),
  computed: {
    darfVerwalten() {
      const { save } = this.$store.state.main;
      return Boolean(save.user && Array.isArray(save.user.rechte)
        && save.user.rechte.includes('s|005|passwordreset')
        && (save.user.rechte.includes('x') || save.user.rechte.includes('x|005|set')));
    },
    requestRunning() {
      return this.loading || this.saving || this.testing;
    },
    placeholderLabel() {
      return PLACEHOLDERS;
    },
    normalizedForm() {
      return {
        enabled: Boolean(this.form.enabled),
        emailParameterIds: this.form.emailParameterIds
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value > 0)
          .sort((first, second) => first - second),
        baseUrl: String(this.form.baseUrl || '').trim().replace(/\/+$/, ''),
        tokenTtlMinutes: Number(this.form.tokenTtlMinutes),
        smtp: {
          name: String(this.form.smtp.name || '').trim(),
          mail: String(this.form.smtp.mail || '').trim(),
          host: String(this.form.smtp.host || '').trim(),
          port: Number(this.form.smtp.port),
          secure: String(this.form.smtp.secure || ''),
          auth: Boolean(this.form.smtp.auth),
          user: String(this.form.smtp.user || '').trim(),
        },
        subject: String(this.form.subject || '').trim(),
        bodyHtml: String(this.form.bodyHtml || ''),
        buttonText: String(this.form.buttonText || '').trim(),
        buttonColor: String(this.form.buttonColor || '').trim().toUpperCase(),
        footerHtml: String(this.form.footerHtml || ''),
      };
    },
    hasChanges() {
      return this.loaded && (JSON.stringify(this.normalizedForm) !== this.loadedSnapshot
        || this.smtpPassword !== '' || this.clearSmtpPassword);
    },
    emailParameterRules() {
      return [
        (value) => !this.form.enabled || (Array.isArray(value) && value.length > 0)
          || 'Bitte mindestens ein E-Mail-Feld auswählen.',
      ];
    },
    baseUrlRules() {
      return [
        (value) => !this.form.enabled || Boolean(String(value || '').trim())
          || 'Bitte die öffentliche App-Adresse angeben.',
        (value) => !value || /^https:\/\/[^\s/?#]+(?::\d+)?(?:\/[^?#\s]*)?$/i.test(String(value).trim())
          || 'Bitte eine vollständige HTTPS-Adresse ohne Query oder Fragment angeben.',
      ];
    },
    smtpNameRules() {
      return [
        (value) => !this.form.enabled || Boolean(String(value || '').trim())
          || 'Bitte einen Absendernamen angeben.',
      ];
    },
    smtpMailRules() {
      return [
        (value) => !this.form.enabled || Boolean(String(value || '').trim())
          || 'Bitte eine Absender-E-Mail-Adresse angeben.',
        (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())
          || 'Bitte eine gültige E-Mail-Adresse angeben.',
      ];
    },
    smtpHostRules() {
      return [
        (value) => !this.form.enabled || Boolean(String(value || '').trim())
          || 'Bitte einen SMTP-Host angeben.',
        (value) => !value || /^[A-Za-z0-9.-]+$/.test(String(value).trim())
          || 'Der SMTP-Host enthält ungültige Zeichen.',
      ];
    },
    smtpPortRules() {
      return [
        (value) => (Number.isInteger(Number(value))
          && Number(value) >= 1 && Number(value) <= 65535)
          || 'Bitte einen Port zwischen 1 und 65535 angeben.',
      ];
    },
    smtpUserRules() {
      return [
        (value) => !this.form.enabled || !this.form.smtp.auth
          || Boolean(String(value || '').trim()) || 'Bitte den SMTP-Benutzernamen angeben.',
      ];
    },
    smtpPasswordRules() {
      return [
        () => !this.form.enabled || !this.form.smtp.auth
          || (this.summary.passwordConfigured && !this.clearSmtpPassword)
          || this.smtpPassword !== ''
          || 'Bitte ein SMTP-Passwort angeben.',
      ];
    },
    subjectRules() {
      return [
        (value) => !this.form.enabled || Boolean(String(value || '').trim())
          || 'Bitte einen Betreff angeben.',
        (value) => !/[\r\n\0]/.test(String(value || ''))
          || 'Der Betreff enthält ungültige Zeichen.',
      ];
    },
    buttonTextRules() {
      return [
        (value) => !this.form.enabled || Boolean(String(value || '').trim())
          || 'Bitte eine Schaltflächenbeschriftung angeben.',
      ];
    },
    buttonColorRules() {
      return [
        (value) => /^#[0-9A-Fa-f]{6}$/.test(String(value || ''))
          || 'Bitte eine Farbe im Format #1976D2 angeben.',
      ];
    },
    currentPasswordRules() {
      return [
        (value) => Boolean(value) || 'Bitte Ihr aktuelles Login-Passwort eingeben.',
      ];
    },
    validPreviewColor() {
      return /^#[0-9A-Fa-f]{6}$/.test(this.form.buttonColor)
        ? this.form.buttonColor : '#1976D2';
    },
    previewDocument() {
      const replacements = {
        '{{APP_NAME}}': 'OGS digital',
        '{{DISPLAY_NAME}}': 'Max Mustermann',
        '{{USERNAME}}': 'max.mustermann',
        '{{VALID_MINUTES}}': String(this.form.tokenTtlMinutes || 30),
      };
      const applyPlaceholders = (html) => Object.keys(replacements).reduce(
        (result, placeholder) => result.split(placeholder)
          .join(escapeHtml(replacements[placeholder])),
        String(html || ''),
      );
      const buttonText = escapeHtml(this.form.buttonText || 'Passwort zurücksetzen');
      const previewBase = String(this.form.baseUrl || 'https://beispiel.ogs-digital.de')
        .replace(/\/+$/, '');
      const previewLink = escapeHtml(`${previewBase}/passwort-zuruecksetzen#token=…`);
      return `<!doctype html><html lang="de"><head><meta charset="utf-8"><meta name="referrer" content="no-referrer"><style>body{margin:0;padding:24px;background:#f3f5f7;color:#263238;font-family:Arial,sans-serif}.card{max-width:640px;margin:auto;background:#fff;border-radius:10px;padding:30px;box-shadow:0 2px 12px rgba(0,0,0,.08)}.button{display:inline-block;margin:18px 0;padding:13px 22px;border-radius:5px;background:${this.validPreviewColor};color:#fff!important;text-decoration:none;font-weight:bold}.fallback{font-size:13px;color:#607d8b;overflow-wrap:anywhere}.footer{margin-top:24px;padding-top:18px;border-top:1px solid #e0e0e0;color:#607d8b;font-size:13px}img{max-width:100%;height:auto}</style></head><body><div class="card">${applyPlaceholders(this.form.bodyHtml)}<p><a class="button" href="#">${buttonText}</a></p><p class="fallback">Falls die Schaltfläche nicht funktioniert, öffnen Sie diesen Link:<br><a href="#">${previewLink}</a></p><div class="footer">${applyPlaceholders(this.form.footerHtml)}</div></div></body></html>`;
    },
  },
  methods: {
    setMessage(type, message) {
      this.messageType = type;
      this.message = message;
    },
    clearSecrets() {
      this.smtpPassword = '';
      this.clearSmtpPassword = false;
      this.currentPassword = '';
      this.showSmtpPassword = false;
      this.showCurrentPassword = false;
    },
    async sendRequest(action, fields = {}) {
      if (!this.darfVerwalten) {
        throw new Error('Sie sind nicht zur Verwaltung dieser Einstellungen berechtigt.');
      }
      const formData = new FormData();
      formData.append('code', this.$store.state.login.session.code);
      formData.append('action', action);
      Object.keys(fields).forEach((field) => formData.append(field, fields[field]));

      let response;
      try {
        response = await fetch(
          `${process.env.VUE_APP_API_HOST}/settings/password_reset.php`,
          { method: 'POST', body: formData, credentials: 'same-origin' },
        );
      } catch {
        throw new Error('Der Server konnte nicht erreicht werden. Bitte versuchen Sie es erneut.');
      }
      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error('Die Serverantwort konnte nicht verarbeitet werden.');
      }
      if (result.status === 'accessError') {
        this.$store.commit('login/backendErrorSession');
        throw new Error('Ihre Sitzung ist abgelaufen.');
      }
      if (!response.ok || result.status !== 'success') {
        throw new Error(result.message || 'Die Einstellungen konnten nicht verarbeitet werden.');
      }
      return result;
    },
    applySummary(summary) {
      const defaults = defaultForm();
      const smtp = summary && summary.smtp ? summary.smtp : {};
      this.form = {
        enabled: boolValue(summary.enabled),
        emailParameterIds: Array.isArray(summary.emailParameterIds)
          ? summary.emailParameterIds.map((value) => Number(value)) : [],
        baseUrl: String(summary.baseUrl || defaultPublicBaseUrl()),
        tokenTtlMinutes: Number(summary.tokenTtlMinutes || 30),
        smtp: {
          name: String(smtp.name || ''),
          mail: String(smtp.mail || ''),
          host: String(smtp.host || ''),
          port: Number(smtp.port || 465),
          secure: String(smtp.secure === undefined ? 'ssl' : smtp.secure),
          auth: smtp.auth === undefined ? true : boolValue(smtp.auth),
          user: String(smtp.user || ''),
        },
        subject: String(summary.subject !== undefined ? summary.subject : defaults.subject),
        bodyHtml: String(summary.bodyHtml !== undefined ? summary.bodyHtml : defaults.bodyHtml),
        buttonText: String(summary.buttonText !== undefined
          ? summary.buttonText : defaults.buttonText),
        buttonColor: String(summary.buttonColor !== undefined
          ? summary.buttonColor : defaults.buttonColor).toUpperCase(),
        footerHtml: String(summary.footerHtml !== undefined
          ? summary.footerHtml : defaults.footerHtml),
      };
      this.summary = {
        passwordConfigured: boolValue(summary.passwordConfigured),
        smtpConfigured: boolValue(summary.smtpConfigured),
        emailParametersConfigured: boolValue(summary.emailParametersConfigured),
        invalidEmailParameterIds: Array.isArray(summary.invalidEmailParameterIds)
          ? summary.invalidEmailParameterIds.map((value) => Number(value)) : [],
        revision: Number(summary.revision || 0),
        updatedAt: Number(summary.updatedAt || 0),
        lastTestedAt: Number(summary.lastTestedAt || 0),
        lastTestResult: String(summary.lastTestResult || ''),
      };
      this.loadedSnapshot = JSON.stringify(this.normalizedForm);
      this.smtpPassword = '';
      this.clearSmtpPassword = false;
    },
    emailParametersFromStore() {
      const parameters = this.$store.state.main.save.nutzerparameter;
      if (!Array.isArray(parameters)) return [];
      return parameters
        .filter((parameter) => Number(parameter.typ) === 4)
        .map((parameter) => ({ id: Number(parameter.id), name: parameter.name }))
        .sort((first, second) => first.name.localeCompare(second.name, 'de'));
    },
    async loadSettings() {
      this.loading = true;
      this.message = '';
      try {
        const result = await this.sendRequest('settingsGet');
        if (!result.summary || typeof result.summary !== 'object') {
          throw new Error('Der Server hat keine gültigen Einstellungen zurückgegeben.');
        }
        const available = Array.isArray(result.availableEmailParameters)
          ? result.availableEmailParameters.map((parameter) => ({
            id: Number(parameter.id),
            name: String(parameter.name || `E-Mail-Feld ${parameter.id}`),
          })) : this.emailParametersFromStore();
        const knownIds = new Set(available.map((parameter) => parameter.id));
        const invalidIds = Array.isArray(result.summary.invalidEmailParameterIds)
          ? result.summary.invalidEmailParameterIds.map((value) => Number(value)) : [];
        invalidIds.forEach((id) => {
          if (Number.isInteger(id) && id > 0 && !knownIds.has(id)) {
            available.push({ id, name: `Nicht mehr vorhandenes E-Mail-Feld (ID ${id})` });
          }
        });
        this.availableEmailParameters = available;
        this.applySummary(result.summary);
        this.loaded = true;
      } catch (error) {
        this.setMessage('error', error.message || 'Die Einstellungen konnten nicht geladen werden.');
      } finally {
        this.loading = false;
      }
    },
    reloadSettings() {
      if (this.hasChanges) {
        this.setMessage('warning', 'Es gibt ungespeicherte Änderungen. Speichern Sie diese zuerst.');
        return;
      }
      this.clearSecrets();
      this.loadSettings();
    },
    validateCurrentPassword() {
      if (!this.currentPassword) {
        this.setMessage('warning', 'Bitte geben Sie Ihr aktuelles Login-Passwort ein.');
        this.$nextTick(() => {
          if (this.$refs.currentPassword) this.$refs.currentPassword.focus();
        });
        return false;
      }
      return true;
    },
    smtpReady() {
      return Boolean(this.normalizedForm.smtp.name
        && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.normalizedForm.smtp.mail)
        && this.normalizedForm.smtp.host
        && Number.isInteger(this.normalizedForm.smtp.port)
        && this.normalizedForm.smtp.port >= 1
        && this.normalizedForm.smtp.port <= 65535
        && (!this.normalizedForm.smtp.auth
          || (this.normalizedForm.smtp.user && !this.clearSmtpPassword
            && (this.summary.passwordConfigured || this.smtpPassword))));
    },
    requestSettings() {
      const settings = JSON.parse(JSON.stringify(this.normalizedForm));
      if (!settings.smtp.auth || this.clearSmtpPassword) {
        settings.smtp.clearPassword = true;
      } else if (this.smtpPassword !== '') {
        settings.smtp.password = this.smtpPassword;
      }
      return settings;
    },
    handleClearSmtpPassword(clearPassword) {
      if (clearPassword) this.smtpPassword = '';
      if (this.$refs.settingsForm) this.$refs.settingsForm.validate();
    },
    async testSmtp() {
      if (this.requestRunning) return;
      if (!this.smtpReady()) {
        this.setMessage('warning', 'Bitte vervollständigen Sie zuerst die SMTP-Einstellungen.');
        return;
      }
      if (!this.validateCurrentPassword()) return;
      this.testing = true;
      this.message = '';
      try {
        const result = await this.sendRequest('smtpTest', {
          currentPassword: this.currentPassword,
          settings: JSON.stringify(this.requestSettings()),
        });
        this.summary.smtpConfigured = true;
        this.summary.lastTestedAt = result.test && result.test.testedAt
          ? Number(result.test.testedAt) : Math.floor(Date.now() / 1000);
        this.summary.lastTestResult = 'success';
        this.setMessage('success', result.message || 'Die SMTP-Verbindung war erfolgreich.');
      } catch (error) {
        this.setMessage('error', error.message || 'Der SMTP-Test ist fehlgeschlagen.');
      } finally {
        this.currentPassword = '';
        this.testing = false;
      }
    },
    async saveSettings() {
      if (this.requestRunning || !this.hasChanges) return;
      if (!this.$refs.settingsForm || !this.$refs.settingsForm.validate()) {
        this.setMessage('warning', 'Bitte prüfen Sie die markierten Eingaben.');
        return;
      }
      if (!this.validateCurrentPassword()) return;
      this.saving = true;
      this.message = '';
      try {
        const result = await this.sendRequest('settingsSave', {
          currentPassword: this.currentPassword,
          expectedRevision: String(this.summary.revision || 0),
          settings: JSON.stringify(this.requestSettings()),
        });
        if (!result.summary) throw new Error('Die gespeicherten Einstellungen fehlen in der Antwort.');
        this.applySummary(result.summary);
        this.setMessage('success', result.message || 'Die Einstellungen wurden sicher gespeichert.');
      } catch (error) {
        this.setMessage('error', error.message || 'Die Einstellungen konnten nicht gespeichert werden.');
      } finally {
        this.currentPassword = '';
        this.saving = false;
      }
    },
    handleEditorError(message) {
      this.setMessage('error', message || 'Der E-Mail-Inhalt konnte nicht eingefügt werden.');
    },
  },
  created() {
    if (this.darfVerwalten) this.loadSettings();
  },
  beforeDestroy() {
    this.clearSecrets();
  },
};
</script>

<style scoped>
.mail-preview {
  width: 100%;
  min-height: 480px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background: #fff;
}

.color-preview {
  display: inline-block;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 50%;
}
</style>
